// Copyable id pill. Displayed inline in response viewers, flow step
// headers, and the "use in next step" affordances. Keeps the long ids
// readable (truncated middle) and exposes a one-click copy to clipboard.

import { useEffect, useState } from 'react';

interface Props {
  /** The id to display. */
  value: string;
  /** Optional human-readable label rendered before the id (e.g. "project_id"). */
  label?: string;
  /** Optional click handler — useful for "use this id in the next step" actions. */
  onClick?: () => void;
  /** Optional CTA shown after the id (e.g. "Use →"). */
  cta?: string;
  /** Visual variant. */
  variant?: 'light' | 'dark';
  /** Override the truncation behaviour. */
  truncate?: boolean;
}

export function IdChip({ value, label, onClick, cta, variant = 'light', truncate = true }: Props) {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return;
    const t = setTimeout(() => setCopied(false), 1300);
    return () => clearTimeout(t);
  }, [copied]);

  const copy = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
    } catch {
      /* ignore */
    }
  };

  const display = truncate ? truncateMiddle(value, 24) : value;

  const isDark = variant === 'dark';

  const containerStyle: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    fontSize: 11.5,
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
    background: isDark ? 'rgba(255,255,255,0.08)' : '#f1f0ff',
    border: `1px solid ${isDark ? 'rgba(255,255,255,0.18)' : '#cbc4ff'}`,
    color: isDark ? '#cbc4ff' : '#3328aa',
    padding: '3px 4px 3px 8px',
    borderRadius: 9999,
    maxWidth: '100%',
    minWidth: 0,
  };

  const labelStyle: React.CSSProperties = {
    fontFamily:
      'Inter, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
    fontSize: 10,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    color: isDark ? '#a99dff' : '#6256ff',
    paddingRight: 4,
  };

  return (
    <span style={containerStyle} className="lp-truncate">
      {label && <span style={labelStyle}>{label}</span>}
      <span className="lp-truncate" title={value}>
        {display}
      </span>
      <button
        type="button"
        onClick={copy}
        title={copied ? 'Copied!' : 'Copy to clipboard'}
        className="lp-shrink-0"
        style={{
          width: 22,
          height: 22,
          borderRadius: 9999,
          background: isDark ? 'rgba(255,255,255,0.08)' : '#ffffff',
          border: 'none',
          color: isDark ? '#e2e8f0' : '#475569',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {copied ? (
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
          </svg>
        ) : (
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="9" y="9" width="11" height="11" rx="2" />
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 15V5a2 2 0 0 1 2-2h10" />
          </svg>
        )}
      </button>
      {onClick && cta && (
        <button
          type="button"
          onClick={onClick}
          className="lp-shrink-0"
          style={{
            fontFamily: 'Inter, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
            fontSize: 10.5,
            fontWeight: 700,
            padding: '3px 8px',
            borderRadius: 9999,
            background: 'linear-gradient(135deg, #6256ff, #7c3aed)',
            color: '#ffffff',
            border: 'none',
            boxShadow: '0 4px 10px rgba(98,86,255,0.28)',
          }}
        >
          {cta}
        </button>
      )}
    </span>
  );
}

function truncateMiddle(s: string, max: number): string {
  if (s.length <= max) return s;
  const half = Math.floor((max - 1) / 2);
  return `${s.slice(0, half)}…${s.slice(-half)}`;
}

// =============================================================================
// extractIds — finds known id-shaped fields in an arbitrary JSON response
// =============================================================================

const ID_FIELD_NAMES = [
  'id',
  'project_id',
  'document_id',
  'media_id',
  'storyboard_id',
  'asset_id',
  'organization_id',
  'workspace_id',
  'brand_kit_id',
  'saved_settings_id',
  'voice_profile_id',
  'face_profile_id',
  'presenter_profile_id',
  'contact_id',
] as const;

export type KnownIdName = (typeof ID_FIELD_NAMES)[number];

export interface ExtractedId {
  field: KnownIdName | string;
  value: string;
  /** True if this field name is in our known set (renders with a coloured label). */
  known: boolean;
}

export function extractIds(body: unknown): ExtractedId[] {
  const found: ExtractedId[] = [];
  if (!body || typeof body !== 'object') return found;

  const visit = (obj: unknown, depth = 0) => {
    if (depth > 3 || !obj || typeof obj !== 'object') return;
    const o = obj as Record<string, unknown>;
    for (const [k, v] of Object.entries(o)) {
      if (typeof v === 'string' && /^[a-z0-9][a-z0-9_-]{4,}$/i.test(v)) {
        if ((ID_FIELD_NAMES as readonly string[]).includes(k)) {
          found.push({ field: k, value: v, known: true });
        }
      } else if (v && typeof v === 'object') {
        visit(v, depth + 1);
      }
    }
  };
  visit(body);

  // Deduplicate by `field` (keep first occurrence — closest to the root).
  const seen = new Set<string>();
  return found.filter((f) => {
    if (seen.has(f.field)) return false;
    seen.add(f.field);
    return true;
  });
}

/** Returns true if the response body contains a `target_signed_url` /
 *  `target` URL that looks like an S3 presigned PUT — used by the doc
 *  upload flow to surface the next step. */
export function extractSignedUrl(body: unknown): string | null {
  if (!body || typeof body !== 'object') return null;
  const o = body as Record<string, unknown>;
  const candidates = ['target_signed_url', 'target', 'signed_url', 'upload_url'];
  for (const k of candidates) {
    const v = o[k];
    if (typeof v === 'string' && /^https?:\/\//.test(v)) return v;
  }
  return null;
}
